import {
  resolveOutputFormat,
  serializeStructured,
  type OutputFormat,
} from './services/output-format.js';

// BL621 Phase 3 — `Output` is now format-aware (table | json | yaml | wide)
// while keeping the pre-BL621 method surface (`json` / `human` / `quiet` /
// `warn`) so the dozens of existing call sites need no change. Read verbs that
// opt into `-o` thread `output` through `fromOpts`; everything else keeps
// passing `{ json, quiet }` and behaves exactly as before.
export interface OutputOpts {
  json?: boolean;
  quiet?: boolean;
  /** Raw `-o` / `--output` value (BL621). Wins over `json` when present. */
  output?: string;
}

type OutputMode = OutputFormat | 'quiet';

export class Output {
  private readonly _mode: OutputMode;

  constructor(opts: OutputOpts) {
    // `quiet` is its own mode, but only when no explicit format was requested —
    // an explicit `-o json` / `--json` / env override always wins so a script
    // can ask for structured output even from a quiet-capable verb.
    if (opts.quiet && !opts.output && !opts.json && !process.env.CLOUDGRID_OUTPUT) {
      this._mode = 'quiet';
    } else {
      this._mode = resolveOutputFormat({ output: opts.output, json: opts.json });
    }
  }

  static fromOpts(opts: OutputOpts): Output {
    return new Output(opts);
  }

  get mode(): OutputMode {
    return this._mode;
  }

  /** json | yaml — the machine-readable formats. */
  get isStructured(): boolean {
    return this._mode === 'json' || this._mode === 'yaml';
  }

  /** table | wide — the human-facing formats (an interactive prompt is fine). */
  get isHuman(): boolean {
    return this._mode === 'table' || this._mode === 'wide';
  }

  /** `-o wide` was requested — renderers can add extra columns. */
  get isWide(): boolean {
    return this._mode === 'wide';
  }

  /**
   * Emit a human-readable block. Runs for both `table` and `wide`; a renderer
   * that wants extra columns in wide mode checks {@link isWide} itself.
   */
  human(produce: () => string | void): void {
    if (!this.isHuman) return;
    const s = produce();
    if (typeof s === 'string') process.stdout.write(s.endsWith('\n') ? s : s + '\n');
  }

  /**
   * Emit a structured payload. Serialized as JSON or YAML per the active
   * format. The name is kept (rather than `data`) so existing call sites work
   * unchanged; `data` is provided as a clearer alias for new call sites.
   */
  json(produce: () => unknown): void {
    if (this._mode !== 'json' && this._mode !== 'yaml') return;
    process.stdout.write(serializeStructured(this._mode, produce()));
  }

  /** Alias of {@link json} that reads better where json *or* yaml is emitted. */
  data(produce: () => unknown): void {
    this.json(produce);
  }

  quiet(produce: () => string): void {
    if (this._mode !== 'quiet') return;
    process.stdout.write(produce() + '\n');
  }

  warn(msg: string): void {
    if (this._mode === 'quiet' || this.isStructured) return;
    process.stderr.write(msg + '\n');
  }
}
